<template>
  <BaseCenteredModal
    v-model:is-modal-open="localIsModalOpen"
    size="large"
    :title="$t('attributes.payments')"
    @close="$emit('close')"
  >
    <template #body>
      <div class="flex h-[60vh] w-full flex-col gap-4">
        <div
          v-if="loading"
          class="flex flex-1 flex-col items-center justify-center gap-2"
        >
          <LoadingComponent />
        </div>
        <div
          v-else-if="payments.length === 0 && otherPayments.length === 0"
          class="flex flex-1 flex-row items-center justify-center text-slate-300"
        >
          {{
            $t('global.there_is_no_yet', {
              settings: $t('attributes.payments')
            })
          }}
        </div>
        <template v-else>
          <div class="flex min-h-0 flex-1 flex-col gap-4">
            <div class="flex shrink-0 flex-row items-center gap-2">
              <button
                class="flex flex-row items-center gap-2 rounded border px-2 py-0.5 text-base"
                :class="{
                  'bg-primary text-contrast-primary':
                    paymentListView == 'current'
                }"
                @click="paymentListView = 'current'"
              >
                <span>
                  {{ $t('global.payment.on_this_document') }}
                </span>
                <span
                  class="flex aspect-square h-4 w-4 items-center justify-center rounded-full border bg-secondary text-xxs text-contrast-secondary"
                >
                  {{ payments.length }}
                </span>
              </button>
              <button
                class="flex flex-row items-center gap-2 rounded border px-2 py-0.5 text-base"
                :class="{
                  'bg-primary text-contrast-primary': paymentListView == 'other'
                }"
                @click="paymentListView = 'other'"
              >
                <span>
                  {{ $t('global.payment.other_payments') }}
                </span>
                <span
                  class="flex aspect-square h-4 w-4 items-center justify-center rounded-full border bg-secondary text-xxs text-contrast-secondary"
                >
                  {{ otherPayments.length }}
                </span>
              </button>
              <button
                class="flex flex-row items-center gap-2 rounded border px-2 py-0.5 text-base"
                :class="{
                  'bg-primary text-contrast-primary': paymentListView == 'chift'
                }"
                @click="showChiftComparison"
              >
                <span>
                  {{ $t('invoicing.chift_tab_title') }}
                </span>
                <span
                  v-if="chiftComparison?.available"
                  class="flex aspect-square h-4 w-4 items-center justify-center rounded-full border bg-secondary text-xxs text-contrast-secondary"
                >
                  {{ chiftComparison.accounting_payments.length }}
                </span>
              </button>
              <div v-if="paymentListView !== 'chift'" class="ml-auto">
                <WalkmanButtonIcon
                  v-model="displayMode"
                  :options="[
                    {
                      value: 'list',
                      icon: ['fal', 'list'],
                      title: $t('attributes.list')
                    },
                    {
                      value: 'card',
                      icon: ['fas', 'address-card'],
                      title: $t('global.card')
                    }
                  ]"
                />
              </div>
            </div>
            <div
              v-if="paymentListView == 'current'"
              class="flex min-h-0 flex-1 flex-col gap-3"
            >
              <div class="flex-1 overflow-y-auto">
                <PaymentListTable
                  v-if="displayMode === 'list'"
                  :payments="payments"
                  :currency="invoice?.currency"
                />
                <div v-else class="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <PaymentModalCard2
                    v-for="payment in payments"
                    :key="payment.id"
                    :payment="payment"
                    @delete="deletePayment"
                    @link-payment="linkPayment"
                    @payment-updated="fetchPaymentsByInvoicingId"
                  />
                </div>
              </div>
              <div
                class="flex shrink-0 flex-row justify-between rounded-md bg-primary p-3 text-lg font-bold text-white uppercase"
              >
                <p>{{ $t('global.total') }}</p>
                <p>
                  {{
                    $h.formatCurrency(invoiceTotalPayments, invoice.currency)
                  }}
                </p>
              </div>
            </div>
            <div v-if="paymentListView === 'other'" class="flex flex-col gap-3">
              <PaymentListTable
                v-if="displayMode === 'list'"
                :payments="otherPayments"
                :currency="invoice?.currency"
              />
              <div v-else class="grid grid-cols-1 gap-2 md:grid-cols-2">
                <PaymentModalCard2
                  v-for="payment in otherPayments"
                  :key="payment.id"
                  :payment="payment"
                  not-linked-payment
                  @link-payment="linkPayment"
                  @payment-updated="fetchPaymentsByInvoicingId"
                />
              </div>
            </div>
            <PaymentChiftComparisonPanel
              v-if="paymentListView === 'chift'"
              :comparison="chiftComparison"
              :loading="chiftLoading"
              :currency="invoice.currency"
              :invoice-id="invoice.id"
            />
          </div>
        </template>
      </div>
    </template>
  </BaseCenteredModal>
</template>

<script>
import PaymentModalCard2 from '@ui/modal/payment/components/PaymentModalCard2'
import PaymentChiftComparisonPanel from '@ui/modal/payment/components/PaymentChiftComparisonPanel.vue'
import PaymentListTable from '@ui/modal/payment/PaymentListTable.vue'
import WalkmanButtonIcon from '@c/WalkmanButtonIcon.vue'
import { mapActions, mapGetters } from 'vuex'
import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'
import LoadingComponent from '@c/TheLoadingComponent.vue'

export default {
  name: 'PaymentListModal',
  components: {
    LoadingComponent,
    BaseCenteredModal,
    PaymentModalCard2,
    PaymentChiftComparisonPanel,
    PaymentListTable,
    WalkmanButtonIcon
  },
  props: {
    isModalOpen: {
      type: Boolean,
      required: true
    }
  },
  emits: ['close', 'cardSelected', 'update:is-modal-open'],
  data() {
    return {
      payments: [],
      otherPayments: [],
      loading: false,
      paymentListView: 'current',
      displayMode: 'card',
      chiftComparison: null,
      chiftLoading: false
    }
  },
  computed: {
    ...mapGetters({
      invoice: 'invoicing/getInvoice'
    }),
    invoiceTotalPayments() {
      if (this.payments.length === 0) {
        return 0
      }
      return this.payments
        ?.reduce((total, payment) => total + parseFloat(payment.amount), 0.0)
        .toFixed(2)
    },
    localIsModalOpen: {
      get() {
        return this.isModalOpen
      },
      set(value) {
        this.$emit('update:is-modal-open', value)
      }
    }
  },
  watch: {
    isModalOpen(newValue, oldValue) {
      if (newValue) {
        this.fetchPaymentsByInvoicingId()

        return
      }

      this.chiftComparison = null
      this.paymentListView = 'current'
    }
  },
  methods: {
    ...mapActions({
      processUnlinkPaymentAction: 'invoicing/unlinkPayment',
      processUndoDeletePaymentAction: 'invoicing/undoDeletePayment',
      processFetchInvoiceByIdAction: 'invoicing/fetchInvoiceID',
      processFetchPaymentsByInvoicingIdAction:
        'invoicing/fetchPaymentsByInvoicingId',
      processFetchInvoiceChiftPaymentsAction:
        'invoicing/fetchInvoiceChiftPayments',
      processLinkPaymentToInvoiceAction: 'invoicing/linkPaymentToInvoice'
    }),
    showChiftComparison() {
      this.paymentListView = 'chift'

      // Called only on the first visit to the tab: the accounting software is a
      // remote service, and re-hitting it every time someone switches tabs would
      // be both slow and a good way to meet a rate limit. The cache is dropped
      // whenever the payments change or the modal closes, so it cannot go stale.
      if (this.chiftComparison !== null || this.chiftLoading) {
        return
      }

      this.chiftLoading = true
      this.processFetchInvoiceChiftPaymentsAction({
        invoice_id: this.invoice.id
      })
        .then((response) => {
          this.chiftComparison = response
        })
        .catch(() => {
          this.chiftComparison = { available: false, reason: 'unreachable' }
        })
        .finally(() => (this.chiftLoading = false))
    },
    fetchPaymentsByInvoicingId() {
      this.loading = true
      // Any change to the payments makes the comparison obsolete.
      this.chiftComparison = null
      this.processFetchPaymentsByInvoicingIdAction({
        invoice_id: this.invoice.id
      })
        .then((response) => {
          this.payments = response.payments
          this.otherPayments = response.other_payments
          if (
            this.paymentListView === 'other' &&
            this.otherPayments.length === 0
          ) {
            this.paymentListView = 'current'
          }
        })
        .finally((_) => (this.loading = false))
    },
    handleSelectPayment(payment_id) {
      this.$emit('cardSelected', payment_id)
    },
    linkPayment(payment_id) {
      this.processLinkPaymentToInvoiceAction({
        invoice_id: this.invoice.id,
        payment_id: payment_id
      }).then((response) => {
        if (response.success) {
          this.fetchPaymentsByInvoicingId()
          this.processFetchInvoiceByIdAction({
            id: this.invoice.id
          })
        }
      })
    },
    deletePayment(payment_id, deletion_type) {
      this.processUnlinkPaymentAction({
        payment_id: payment_id,
        invoice_id: this.invoice.id,
        deletion_type: deletion_type
      }).then((response) => {
        if (response.success) {
          this.fetchPaymentsByInvoicingId()
          this.processFetchInvoiceByIdAction({
            id: this.invoice.id
          })
        }
      })
    },
    undoDeletePayment(payment_id) {
      this.$h.toastAction(
        'delete',
        this.$t('toasts.deleted_m', { var: this.$t('attributes.payment') }),
        null,
        [
          {
            title: this.$t('button.cancel'),
            class: 'text-red-700',
            action: () => {
              // process de la restauration
              this.processUndoDeletePaymentAction({
                payment_id: payment_id,
                invoice_id: this.invoice.id
              }).then((response) => {
                // on affiche de toast success
                this.$h.toastSuccess(
                  this.$t('toasts.reseted_m', {
                    var: this.$t('attributes.payment')
                  })
                )
              })
            }
          }
        ]
      )
    }
  }
}
</script>
